import { ErrorBoundary } from "@/components/ErrorBoundary/ErrorBoundary";
import { Providers } from "@/components/Providers/Providers";
import { RouteError } from "@/components/RouteError/RouteError";
import { SiteFooter } from "@/components/SiteFooter/SiteFooter";
import { ToastList } from "@/components/Toasts/Toasts";
import { routeLoaderApiClient } from "@/lib/api";
import * as Sentry from "@sentry/react";
import { HeadContent, Outlet, Scripts, createRootRoute } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { clsx } from "clsx";
import { useEffect } from "react";
import css from "./__root.module.scss";

export const Route = createRootRoute({
  errorComponent: RootErrorComponent,
  loader: async () => {
    const [systemResponse, customerResponse, userResponse] = await Promise.all([
      routeLoaderApiClient.system.$get(),
      routeLoaderApiClient.realtor.$get(),
      routeLoaderApiClient.user.$get(),
    ]);

    if (systemResponse.ok === false) {
      throw new Error("Failed to fetch system");
    }

    if (customerResponse.ok === false) {
      throw new Error("Failed to fetch customer");
    }

    if (userResponse.ok === false) {
      throw new Error("Failed to fetch user");
    }

    const system = await systemResponse.json();
    const customer = await customerResponse.json();
    const { user } = await userResponse.json();

    return {
      system,
      customer,
      user,
    };
  },
  head: () => ({
    meta: [
      {
        title: "Alice & Bob",
      },
      {
        property: "og:image",
        content: "/assets/opengraph-image.gif",
      },
      {
        property: "og:image:width",
        content: "1200",
      },
      {
        property: "og:image:height",
        content: "600",
      },
    ],
    links: [
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/assets/apple-touch-icon.png",
      },
      {
        rel: "icon",
        type: "image/svg",
        href: "/assets/favicon.svg",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "96x96",
        href: "/assets/favicon-96x96.png",
      },

      { rel: "manifest", href: "/assets/manifest.json" },
    ],
  }),
  component: Page,
});

// We need this component in addition to our Sentry ErrorBoundary because errors
// thrown in the root route's loader are caught by TanStack Router before
// Sentry's ErrorBoundary could catch it.
function RootErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return <RouteError error={error} stacktrace={error instanceof Error ? error.stack : undefined} onReset={reset} />;
}

function Page() {
  const { customer, system, user } = Route.useLoaderData();

  return (
    <ErrorBoundary>
      <Providers customer={customer} system={system} user={user}>
        <HeadContent />

        <div className={css.layout}>
          <ErrorBoundary>
            <div className={clsx(css.main, "l-wrapper")}>
              <Outlet />
            </div>
          </ErrorBoundary>

          <SiteFooter />
        </div>

        <ToastList />
        <TanStackRouterDevtools position="bottom-right" />
        <Scripts />
      </Providers>
    </ErrorBoundary>
  );
}
